import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import SiteFooter from "../components/SiteFooter";
import { useLang } from "../i18n/LanguageContext";

const Global = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f0ebe3; font-family: 'Pretendard', "Noto Sans KR", sans-serif; }
`;
const Wrap = styled.div`max-width: 720px; margin: 0 auto; padding: 40px 20px 80px;`;
const BackBtn = styled.button`
  background: none; border: none; color: #9c7b5a; font-size: 0.85rem;
  font-weight: 700; cursor: pointer; font-family: inherit;
  display: flex; align-items: center; gap: 4px; margin-bottom: 28px; padding: 0;
  &:hover { color: #3d2b1f; }
`;
const PageTitle = styled.h1`
  font-size: 1.5rem; font-weight: 900; color: #3d2b1f; margin-bottom: 6px;
`;
const Updated = styled.p`font-size: 0.78rem; color: #9c7b5a; margin-bottom: 32px;`;
const Section = styled.section`margin-bottom: 28px;`;
const STitle = styled.h2`
  font-size: 1rem; font-weight: 800; color: #3d2b1f;
  margin-bottom: 10px; padding-bottom: 6px;
  border-bottom: 1.5px solid #e0d5c8;
`;
const P = styled.p`
  font-size: 0.85rem; color: #5a4a3a; line-height: 1.9; margin-bottom: 8px;
`;
const UL = styled.ul`
  font-size: 0.85rem; color: #5a4a3a; line-height: 1.9;
  padding-left: 18px; margin-bottom: 8px;
  li { margin-bottom: 4px; }
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 0.82rem;
  color: #5a4a3a; margin-bottom: 10px;
  th, td { border: 1px solid #e0d5c8; padding: 8px 10px; text-align: left; }
  th { background: #faf5ef; font-weight: 700; color: #3d2b1f; }
`;

/* ── 이용약관 ──────────────────────────────────────────────────────────── */
function Terms() {
  const { lang } = useLang();
  if (lang === "en") {
    return (
      <>
        <PageTitle>Terms of Service</PageTitle>
        <Updated>Effective: January 1, 2025 | Last updated: January 1, 2025</Updated>

        <Section>
          <STitle>Article 1 (Purpose)</STitle>
          <P>These Terms set forth the conditions and procedures for using the Wooyeon Saju service (the "Service") operated by Wooyeon (the "Company"), as well as the rights, obligations, and responsibilities between users and the Company.</P>
        </Section>

        <Section>
          <STitle>Article 2 (Definitions)</STitle>
          <UL>
            <li><b>Service</b>: The Four Pillars (saju) analysis and fortune report service based on Myeongri (Chinese astrology) provided by the Company.</li>
            <li><b>User</b>: A person who agrees to these Terms and uses the Service.</li>
            <li><b>Content</b>: All information provided through the Service, including saju analysis results and fortune reports.</li>
            <li><b>Paid Content</b>: Detailed analysis reports that can be viewed after payment.</li>
          </UL>
        </Section>

        <Section>
          <STitle>Article 3 (Description of the Service)</STitle>
          <P>The Company provides the following services.</P>
          <UL>
            <li>Fortune analysis based on the Four Pillars of Destiny (overall fortune, new year fortune, reconciliation fortune, marriage fortune, love compatibility, in-depth compatibility, business fortune, career fortune)</li>
            <li>A free preview, with the full report available for viewing after payment</li>
            <li>Delivery of reports via email</li>
          </UL>
          <P>The Service provides reference information based on traditional Myeongri and does not replace professional judgment in fields such as medicine, law, or finance.</P>
        </Section>

        <Section>
          <STitle>Article 4 (Fees and Payment)</STitle>
          <P>The fees for Paid Content follow the amounts displayed on the Service screen. Payments are made through Toss Payments by credit card, debit card, or simple payment methods (Toss Pay, Kakao Pay, etc.).</P>
          <P>Once payment is completed, a link to view the full report is sent immediately by email, and the report is valid for 7 days from the date of payment.</P>
        </Section>

        <Section>
          <STitle>Article 5 (Withdrawal of Subscription and Refunds)</STitle>
          <P>Refunds for Paid Content are governed by the separate Refund Policy. Due to the nature of digital content, refunds may, in principle, be restricted once a report has been viewed. For details, please refer to the Refund Policy.</P>
        </Section>

        <Section>
          <STitle>Article 6 (Intellectual Property Rights)</STitle>
          <P>The copyrights and intellectual property rights in all Content provided through the Service belong to the Company. Users may not reproduce, transmit, publish, distribute, broadcast, or otherwise use the information obtained from the Service, nor allow third parties to use it, without the Company's prior consent.</P>
        </Section>

        <Section>
          <STitle>Article 7 (Disclaimer)</STitle>
          <P>The saju and fortune analysis results provided by the Company are reference information based on traditional Myeongri and do not definitively predict or guarantee the actual future. Users are solely responsible for the outcomes of decisions made based on the analysis results.</P>
          <P>The Company shall not be liable for any interruption of the Service due to force majeure events such as natural disasters or system failures.</P>
        </Section>

        <Section>
          <STitle>Article 8 (Governing Law and Jurisdiction)</STitle>
          <P>These Terms shall be interpreted in accordance with the laws of the Republic of Korea, and any dispute arising from the use of the Service shall be brought before the competent court of first instance under the Civil Procedure Act.</P>
        </Section>
      </>
    );
  }
  return (
    <>
      <PageTitle>이용약관</PageTitle>
      <Updated>시행일: 2026년 5월 1일 | 최종 수정: 2026년 5월 1일</Updated>

      <Section>
        <STitle>제1조 (목적)</STitle>
        <P>이 약관은 우연(이하 "회사")이 운영하는 우연 사주 서비스(이하 "서비스")의 이용 조건 및 절차, 이용자와 회사 간의 권리·의무 및 책임 사항을 규정함을 목적으로 합니다.</P>
      </Section>

      <Section>
        <STitle>제2조 (용어의 정의)</STitle>
        <UL>
          <li><b>서비스</b>: 회사가 제공하는 명리학 기반 사주 분석 및 운세 리포트 서비스</li>
          <li><b>이용자</b>: 본 약관에 동의하고 서비스를 이용하는 자</li>
          <li><b>콘텐츠</b>: 서비스를 통해 제공되는 사주 분석 결과, 운세 리포트 등 일체의 정보</li>
          <li><b>유료 콘텐츠</b>: 결제 후 열람 가능한 상세 분석 리포트</li>
        </UL>
      </Section>

      <Section>
        <STitle>제3조 (서비스의 내용)</STitle>
        <P>회사는 다음의 서비스를 제공합니다.</P>
        <UL>
          <li>사주팔자 기반 운세 분석 (종합운세, 신년운세, 연애운, 연애궁합, 심층궁합, 직업운, 사업(재물운), 건강운)</li>
          <li>미리보기 무료 제공 및 결제 후 전체 리포트 열람</li>
          <li>이메일을 통한 리포트 발송</li>
        </UL>
        <P>서비스는 전통 명리학에 기반한 참고용 정보를 제공하며, 의료·법률·재무 등 전문 분야의 판단을 대체하지 않습니다.</P>
      </Section>

      <Section>
        <STitle>제4조 (이용요금 및 결제)</STitle>
        <P>유료 콘텐츠의 이용요금은 서비스 화면에 표시된 금액을 따릅니다. 결제는 토스페이먼츠를 통해 신용카드, 체크카드, 간편결제(토스페이, 카카오페이 등) 방식으로 이루어집니다.</P>
        <P>결제가 완료되면 즉시 전체 리포트 열람 링크가 이메일로 발송되며, 리포트는 결제일로부터 7일간 유효합니다.</P>
      </Section>

      <Section>
        <STitle>제5조 (청약철회 및 환불)</STitle>
        <P>유료 콘텐츠의 환불은 별도의 환불정책을 따릅니다. 디지털 콘텐츠의 특성상 리포트를 열람한 경우 원칙적으로 환불이 제한될 수 있습니다. 상세 내용은 환불정책을 확인하시기 바랍니다.</P>
      </Section>

      <Section>
        <STitle>제6조 (지식재산권)</STitle>
        <P>서비스를 통해 제공되는 모든 콘텐츠의 저작권 및 지식재산권은 회사에 귀속됩니다. 이용자는 서비스에서 얻은 정보를 회사의 사전 동의 없이 복제, 전송, 출판, 배포, 방송 등 방법으로 이용하거나 제3자에게 이용하게 해서는 안 됩니다.</P>
      </Section>

      <Section>
        <STitle>제7조 (면책사항)</STitle>
        <P>회사가 제공하는 사주 및 운세 분석 결과는 전통 명리학에 기반한 참고 정보이며, 실제 미래를 확정적으로 예측하거나 보장하지 않습니다. 이용자는 분석 결과를 바탕으로 한 의사 결정의 결과에 대해 스스로 책임을 집니다.</P>
        <P>회사는 천재지변, 시스템 장애 등 불가항력적 사유로 인한 서비스 중단에 대해 책임을 지지 않습니다.</P>
      </Section>

      <Section>
        <STitle>제8조 (준거법 및 재판관할)</STitle>
        <P>이 약관은 대한민국 법률에 따라 해석되며, 서비스 이용으로 발생한 분쟁은 민사소송법상 관할 법원을 제1심 법원으로 합니다.</P>
      </Section>
    </>
  );
}

/* ── 개인정보 처리방침 ────────────────────────────────────────────────── */
function Privacy() {
  const { lang } = useLang();
  if (lang === "en") {
    return (
      <>
        <PageTitle>Privacy Policy</PageTitle>
        <Updated>Effective: January 1, 2025 | Last updated: January 1, 2025</Updated>

        <P>Wooyeon (the "Company") establishes and discloses the following Privacy Policy in accordance with the Personal Information Protection Act, in order to protect users' personal information and rights and to promptly and smoothly handle related grievances.</P>

        <Section>
          <STitle>Article 1 (Items of Personal Information Collected and Methods of Collection)</STitle>
          <Table>
            <thead>
              <tr><th>Item</th><th>Required/Optional</th><th>Method of Collection</th></tr>
            </thead>
            <tbody>
              <tr><td>Name</td><td>Required</td><td>Entered directly when applying for the Service</td></tr>
              <tr><td>Date of birth, time of birth</td><td>Required</td><td>Entered directly when applying for the Service</td></tr>
              <tr><td>Gender</td><td>Required</td><td>Entered directly when applying for the Service</td></tr>
              <tr><td>Email address</td><td>Optional</td><td>Entered directly when applying for the Service</td></tr>
              <tr><td>Payment information (card issuer name, approval number)</td><td>Required (upon payment)</td><td>Provided by the payment gateway (Toss Payments)</td></tr>
            </tbody>
          </Table>
        </Section>

        <Section>
          <STitle>Article 2 (Purposes of Collecting and Using Personal Information)</STitle>
          <UL>
            <li>Providing the saju analysis service and generating result reports</li>
            <li>Processing payments and sending result reports by email</li>
            <li>Responding to customer inquiries and handling disputes</li>
            <li>Statistical analysis to improve service quality (in a non-identifiable form)</li>
          </UL>
        </Section>

        <Section>
          <STitle>Article 3 (Retention and Use Period of Personal Information)</STitle>
          <P>As a rule, collected personal information is destroyed immediately after the purpose of providing the Service has been achieved; however, the following information is retained for the periods specified below for the stated reasons.</P>
          <Table>
            <thead>
              <tr><th>Retained Item</th><th>Legal Basis</th><th>Retention Period</th></tr>
            </thead>
            <tbody>
              <tr><td>Records on contracts or withdrawal of subscription</td><td>Act on Consumer Protection in Electronic Commerce</td><td>5 years</td></tr>
              <tr><td>Records on payment and supply of goods</td><td>Act on Consumer Protection in Electronic Commerce</td><td>5 years</td></tr>
              <tr><td>Records on consumer complaints or dispute resolution</td><td>Act on Consumer Protection in Electronic Commerce</td><td>3 years</td></tr>
            </tbody>
          </Table>
        </Section>

        <Section>
          <STitle>Article 4 (Provision of Personal Information to Third Parties)</STitle>
          <P>As a rule, the Company does not provide users' personal information to outside parties. However, the minimum necessary information (name, email) may be provided to Toss Payments Co., Ltd. for payment processing, and it is used solely for payment purposes.</P>
        </Section>

        <Section>
          <STitle>Article 5 (Outsourcing of Personal Information Processing)</STitle>
          <Table>
            <thead>
              <tr><th>Outsourced Provider</th><th>Outsourced Task</th></tr>
            </thead>
            <tbody>
              <tr><td>Toss Payments Co., Ltd.</td><td>Payment processing and management of payment information</td></tr>
              <tr><td>Google (Gmail)</td><td>Email delivery service</td></tr>
            </tbody>
          </Table>
        </Section>

        <Section>
          <STitle>Article 6 (Rights and Obligations of the Data Subject)</STitle>
          <P>Users may exercise the following rights at any time.</P>
          <UL>
            <li>Request to access personal information</li>
            <li>Request to correct errors, if any</li>
            <li>Request for deletion (except where there is a legal obligation to retain the information)</li>
            <li>Request to suspend processing</li>
          </UL>
          <P>These rights may be exercised by emailing the Personal Information Protection Officer below.</P>
        </Section>

        <Section>
          <STitle>Article 7 (Personal Information Protection Officer)</STitle>
          <Table>
            <thead><tr><th>Item</th><th>Details</th></tr></thead>
            <tbody>
              <tr><td>Person in charge</td><td>Representative</td></tr>
              <tr><td>Email</td><td>wooyeon@wooyeon.co.kr</td></tr>
            </tbody>
          </Table>
          <P>Reports and consultations regarding personal information infringement may be directed to the Personal Information Protection Commission (privacy.go.kr / 182) and the Korea Internet & Security Agency (kisa.or.kr / 118).</P>
        </Section>

        <Section>
          <STitle>Article 8 (Use of Cookies)</STitle>
          <P>The Service may use cookies for the convenience of users. Cookies can be refused through browser settings, but doing so may limit some Service functions.</P>
        </Section>
      </>
    );
  }
  return (
    <>
      <PageTitle>개인정보 처리방침</PageTitle>
      <Updated>시행일: 2026년 5월 1일 | 최종 수정: 2026년 5월 1일</Updated>

      <P>우연(이하 "회사")은 개인정보보호법에 따라 이용자의 개인정보 보호 및 권익을 보호하고 관련 고충을 신속하고 원활하게 처리하기 위하여 다음과 같은 개인정보 처리방침을 수립·공개합니다.</P>

      <Section>
        <STitle>제1조 (수집하는 개인정보 항목 및 수집 방법)</STitle>
        <Table>
          <thead>
            <tr><th>항목</th><th>필수/선택</th><th>수집 방법</th></tr>
          </thead>
          <tbody>
            <tr><td>이름</td><td>필수</td><td>서비스 신청 시 직접 입력</td></tr>
            <tr><td>생년월일, 출생 시간</td><td>필수</td><td>서비스 신청 시 직접 입력</td></tr>
            <tr><td>성별</td><td>필수</td><td>서비스 신청 시 직접 입력</td></tr>
            <tr><td>이메일 주소</td><td>선택</td><td>서비스 신청 시 직접 입력</td></tr>
            <tr><td>결제 정보(카드사명, 승인번호)</td><td>필수(결제 시)</td><td>결제 대행사(토스페이먼츠) 제공</td></tr>
          </tbody>
        </Table>
      </Section>

      <Section>
        <STitle>제2조 (개인정보의 수집 및 이용 목적)</STitle>
        <UL>
          <li>사주 분석 서비스 제공 및 결과 리포트 생성</li>
          <li>결제 처리 및 결과 리포트 이메일 발송</li>
          <li>고객 문의 응대 및 분쟁 처리</li>
          <li>서비스 품질 개선을 위한 통계 분석 (개인 식별 불가 형태)</li>
        </UL>
      </Section>

      <Section>
        <STitle>제3조 (개인정보의 보유 및 이용 기간)</STitle>
        <P>수집된 개인정보는 서비스 제공 목적이 달성된 후 즉시 파기하는 것을 원칙으로 하나, 다음의 정보는 아래의 이유로 명시한 기간 동안 보존합니다.</P>
        <Table>
          <thead>
            <tr><th>보존 항목</th><th>보존 근거</th><th>보존 기간</th></tr>
          </thead>
          <tbody>
            <tr><td>계약 또는 청약철회 등에 관한 기록</td><td>전자상거래법</td><td>5년</td></tr>
            <tr><td>대금결제 및 재화 공급에 관한 기록</td><td>전자상거래법</td><td>5년</td></tr>
            <tr><td>소비자 불만 또는 분쟁 처리 기록</td><td>전자상거래법</td><td>3년</td></tr>
          </tbody>
        </Table>
      </Section>

      <Section>
        <STitle>제4조 (개인정보의 제3자 제공)</STitle>
        <P>회사는 이용자의 개인정보를 원칙적으로 외부에 제공하지 않습니다. 다만, 결제 처리를 위해 토스페이먼츠(주)에 필요 최소한의 정보(성명, 이메일)가 제공될 수 있으며, 이는 결제 목적으로만 사용됩니다.</P>
      </Section>

      <Section>
        <STitle>제5조 (개인정보 처리 위탁)</STitle>
        <Table>
          <thead>
            <tr><th>수탁업체</th><th>위탁 업무</th></tr>
          </thead>
          <tbody>
            <tr><td>토스페이먼츠(주)</td><td>결제 처리 및 결제 정보 관리</td></tr>
            <tr><td>Google (Gmail)</td><td>이메일 발송 서비스</td></tr>
          </tbody>
        </Table>
      </Section>

      <Section>
        <STitle>제6조 (정보주체의 권리·의무)</STitle>
        <P>이용자는 언제든지 다음의 권리를 행사할 수 있습니다.</P>
        <UL>
          <li>개인정보 열람 요청</li>
          <li>오류 등이 있을 경우 정정 요청</li>
          <li>삭제 요청 (단, 법령에 의해 보존 의무가 있는 경우 제외)</li>
          <li>처리 정지 요청</li>
        </UL>
        <P>권리 행사는 아래 개인정보 보호책임자에게 이메일로 요청하실 수 있습니다.</P>
      </Section>

      <Section>
        <STitle>제7조 (개인정보 보호책임자)</STitle>
        <Table>
          <thead><tr><th>항목</th><th>내용</th></tr></thead>
          <tbody>
            <tr><td>담당자</td><td>대표자</td></tr>
            <tr><td>이메일</td><td>wooyeon@wooyeon.co.kr</td></tr>
          </tbody>
        </Table>
        <P>개인정보 침해 관련 신고·상담은 개인정보보호위원회(privacy.go.kr / 182), 한국인터넷진흥원(kisa.or.kr / 118)에 문의하실 수 있습니다.</P>
      </Section>

      <Section>
        <STitle>제8조 (쿠키 사용)</STitle>
        <P>서비스는 이용자 편의를 위해 쿠키(cookie)를 사용할 수 있습니다. 쿠키는 브라우저 설정을 통해 거부할 수 있으나, 일부 서비스 기능이 제한될 수 있습니다.</P>
      </Section>
    </>
  );
}

/* ── 환불정책 ──────────────────────────────────────────────────────────── */
function Refund() {
  const { lang } = useLang();
  if (lang === "en") {
    return (
      <>
        <PageTitle>Refund Policy</PageTitle>
        <Updated>Effective: January 1, 2025 | Last updated: January 1, 2025</Updated>

        <P>The Wooyeon Saju service complies with the Act on Consumer Protection in Electronic Commerce, etc., and operates the following Refund Policy.</P>

        <Section>
          <STitle>Refund Eligibility Period and Conditions</STitle>
          <Table>
            <thead>
              <tr><th>Category</th><th>Refund Eligibility</th><th>Remarks</th></tr>
            </thead>
            <tbody>
              <tr><td>Report not yet viewed after payment</td><td>✅ Full refund</td><td>Within 7 days of payment</td></tr>
              <tr><td>Report already viewed after payment</td><td>❌ No refund</td><td>Due to the nature of digital content</td></tr>
              <tr><td>More than 7 days after payment</td><td>❌ No refund</td><td>Article 17 of the Act on Consumer Protection in Electronic Commerce</td></tr>
              <tr><td>Report not provided due to a service error</td><td>✅ Full refund</td><td>Attributable to the Company</td></tr>
            </tbody>
          </Table>
          <P style={{ fontSize: "0.78rem", color: "#9c7b5a", marginTop: 6 }}>
            * Pursuant to Article 17 of the Act on Consumer Protection in Electronic Commerce, the withdrawal of subscription is restricted once the digital content (report) has been used.
          </P>
        </Section>

        <Section>
          <STitle>How to Request a Refund</STitle>
          <UL>
            <li>Email: wooyeon@wooyeon.co.kr</li>
            <li>Required information: name, date of payment, payment amount, reason for refund</li>
            <li>Processing time: within 3–5 business days</li>
            <li>Refund method: refunded to the original payment method (card cancellation / account transfer)</li>
          </UL>
        </Section>

        <Section>
          <STitle>Grounds for Non-Refund</STitle>
          <UL>
            <li>After the report has been viewed (accessing the report page following completed payment)</li>
            <li>When more than 7 days have passed since the date of payment</li>
            <li>Simple change of mind by the user (after viewing)</li>
            <li>Dissatisfaction with the analysis results due to errors in personal information such as the entered date of birth (re-analysis may be requested)</li>
          </UL>
        </Section>

        <Section>
          <STitle>Re-analysis Service</STitle>
          <P>If a user becomes aware of an error in the information they entered, they may, even after viewing the report, request via email a one-time correction of the information and receive a re-analyzed report.</P>
        </Section>

        <Section>
          <STitle>Contact</STitle>
          <P>Email: wooyeon@wooyeon.co.kr</P>
        </Section>
      </>
    );
  }
  return (
    <>
      <PageTitle>환불정책</PageTitle>
      <Updated>시행일: 2026년 5월 1일 | 최종 수정: 2026년 5월 1일</Updated>

      <P>우연 사주 서비스는 전자상거래 등에서의 소비자 보호에 관한 법률을 준수하며, 아래와 같은 환불정책을 운영합니다.</P>

      <Section>
        <STitle>환불 가능 기간 및 조건</STitle>
        <Table>
          <thead>
            <tr><th>구분</th><th>환불 가능 여부</th><th>비고</th></tr>
          </thead>
          <tbody>
            <tr><td>결제 후 리포트 미열람 상태</td><td>✅ 전액 환불</td><td>결제일로부터 7일 이내</td></tr>
            <tr><td>결제 후 리포트 열람 완료</td><td>❌ 환불 불가</td><td>디지털 콘텐츠 특성상</td></tr>
            <tr><td>결제 후 7일 초과</td><td>❌ 환불 불가</td><td>전자상거래법 제17조</td></tr>
            <tr><td>서비스 오류로 리포트 미제공</td><td>✅ 전액 환불</td><td>회사 귀책사유</td></tr>
          </tbody>
        </Table>
        <P style={{ fontSize: "0.78rem", color: "#9c7b5a", marginTop: 6 }}>
          ※ 전자상거래법 제17조에 따라 디지털 콘텐츠(리포트)를 이용한 경우 청약철회가 제한됩니다.
        </P>
      </Section>

      <Section>
        <STitle>환불 신청 방법</STitle>
        <UL>
          <li>이메일: wooyeon@wooyeon.co.kr</li>
          <li>신청 시 필수 기재: 이름, 결제일, 결제 금액, 환불 사유</li>
          <li>처리 기간: 영업일 기준 3~5일 이내</li>
          <li>환불 방법: 결제 수단으로 원상 환불 (카드 취소 / 계좌 환급)</li>
        </UL>
      </Section>

      <Section>
        <STitle>환불 불가 사유</STitle>
        <UL>
          <li>리포트 열람(결제 완료 후 리포트 페이지 접근) 이후</li>
          <li>결제일로부터 7일이 경과한 경우</li>
          <li>이용자 단순 변심 (열람 이후)</li>
          <li>입력한 생년월일 등 개인정보 오류로 인한 분석 결과 불만족 (재분석 요청 가능)</li>
        </UL>
      </Section>

      <Section>
        <STitle>재분석 서비스</STitle>
        <P>이용자가 입력 정보 오류를 인지한 경우, 리포트 열람 후라도 이메일로 문의 시 1회에 한해 정보 수정 후 재분석 리포트를 제공해 드립니다.</P>
      </Section>

      <Section>
        <STitle>문의처</STitle>
        <P>이메일: wooyeon@wooyeon.co.kr</P>
      </Section>
    </>
  );
}

/* ── 라우터 ────────────────────────────────────────────────────────────── */
const TYPE_MAP: Record<string, { label: string; Component: React.FC }> = {
  terms:   { label: "이용약관",          Component: Terms   },
  privacy: { label: "개인정보 처리방침", Component: Privacy },
  refund:  { label: "환불정책",          Component: Refund  },
};

export default function LegalPage() {
  const { type = "terms" } = useParams<{ type: string }>();
  const navigate = useNavigate();
  const { lang } = useLang();
  const found = TYPE_MAP[type];

  if (!found) {
    navigate("/", { replace: true });
    return null;
  }

  const { Component } = found;
  return (
    <>
      <Global />
      <Wrap>
        <BackBtn onClick={() => navigate(-1)}>{lang === "en" ? "← Back" : "← 뒤로가기"}</BackBtn>
        <Component />
        <SiteFooter />
      </Wrap>
    </>
  );
}
